export function clamp(minimum, value, maximum) {
  return Math.min(maximum, Math.max(minimum, value));
}

export function lerp(start, end, amount) {
  return start + (end - start) * amount;
}

export function damp(current, target, smoothing, deltaSeconds) {
  return lerp(current, target, 1 - Math.exp(-smoothing * deltaSeconds));
}

export function calculateCoverRect(
  viewportWidth,
  viewportHeight,
  videoWidth = 1920,
  videoHeight = 1080
) {
  const scale = Math.max(
    viewportWidth / videoWidth,
    viewportHeight / videoHeight
  );
  const width = videoWidth * scale;
  const height = videoHeight * scale;
  return {
    scale,
    width,
    height,
    offsetX: (viewportWidth - width) / 2,
    offsetY: (viewportHeight - height) / 2
  };
}

export function projectHotspot(
  keyframe,
  viewportWidth,
  viewportHeight,
  videoWidth = 1920,
  videoHeight = 1080
) {
  const cover = calculateCoverRect(
    viewportWidth,
    viewportHeight,
    videoWidth,
    videoHeight
  );
  return {
    x: cover.offsetX + keyframe.cx * cover.width,
    y: cover.offsetY + keyframe.cy * cover.height,
    rx: keyframe.rx * cover.width,
    ry: keyframe.ry * cover.height
  };
}

export function interpolateHotspot(keyframes, time) {
  if (!keyframes.length) {
    return null;
  }
  if (time <= keyframes[0].t) {
    return { ...keyframes[0] };
  }
  const last = keyframes.at(-1);
  if (time >= last.t) {
    return { ...last };
  }

  const nextIndex = keyframes.findIndex((frame) => frame.t >= time);
  const previous = keyframes[nextIndex - 1];
  const next = keyframes[nextIndex];
  const span = Math.max(0.0001, next.t - previous.t);
  const progress = (time - previous.t) / span;
  return {
    t: time,
    cx: lerp(previous.cx, next.cx, progress),
    cy: lerp(previous.cy, next.cy, progress),
    rx: lerp(previous.rx, next.rx, progress),
    ry: lerp(previous.ry, next.ry, progress),
    strength: lerp(
      previous.strength ?? 1,
      next.strength ?? 1,
      progress
    )
  };
}

export function ellipseDistance(point, ellipse) {
  const normalizedX = (point.x - ellipse.x) / Math.max(ellipse.rx, 1);
  const normalizedY = (point.y - ellipse.y) / Math.max(ellipse.ry, 1);
  return Math.sqrt(normalizedX ** 2 + normalizedY ** 2);
}

export function pixelDistance(point, target) {
  return Math.hypot(point.x - target.x, point.y - target.y);
}

export function selectMediaSource({
  sources,
  viewportWidth,
  devicePixelRatio = 1,
  tier = "medium",
  saveData = false,
  effectiveType = "4g"
}) {
  const sorted = [...sources].sort((a, b) => a.width - b.width);
  if (saveData || ["slow-2g", "2g"].includes(effectiveType) || tier === "low") {
    return sorted[0];
  }

  const cap = tier === "high" ? 3840 : 1920;
  const requiredWidth = Math.min(
    viewportWidth * Math.min(devicePixelRatio, 2),
    cap
  );
  return (
    sorted.find((source) => source.width >= requiredWidth) ??
    sorted.at(-1)
  );
}

export function pickInitialMediaSource(selected, sources) {
  const index = sources.findIndex((source) => source.id === selected.id);
  return index > 0 ? sources[index - 1] : selected;
}

export function isInteractiveTarget(target) {
  return Boolean(
    target instanceof Element &&
      target.closest(
        "a, button, input, textarea, select, summary, dialog, [contenteditable], [data-interactive]"
      )
  );
}
